import { championsMap } from '../../../../utils/lol-champions';
import { LOL_LEADERBOARD_TYPES } from '../../../../mixins/games.commons';
import * as ProfileHelpers from './ProfileHelpers';
import dayjs from '../../../../utils/dayjs';

export const LOL_STATS_NAME = {
  winRatio: 'Win ratio',
  lossRatio: 'Loss ratio',
  wins: 'Wins',
  kda: 'KDA',
  goldShare: 'Gold share',
  visionScore: 'Vision score',
  // csd: 'CSD/10',
  damageShare: 'DMG %',
  mastery: 'Level',

  [LOL_LEADERBOARD_TYPES.POINTS]: 'Points',
  [LOL_LEADERBOARD_TYPES.POSITIVITY]: 'Positivity',
  [LOL_LEADERBOARD_TYPES.HOT_STREAK]: 'Hot Streak',
  [LOL_LEADERBOARD_TYPES.OTP_STATUS]: 'Otp Status',
  [LOL_LEADERBOARD_TYPES.ELO_KING]: 'Elo King',
};

export const LOL_STATS_NAME_ABBR = {
  visionScore: 'V/s',
};

export const LOL_QUEUES_STATS_ORDER = [
  'winRatio',
  'kda',
  'goldShare',
  'visionScore',
];

export const LOL_CORE_STATS_ORDER = [
  'kda',
  // 'csd',
  'goldShare',
  'damageShare',
];

export const LOL_MONTHLY_STATS_TOOLTIP_ORDER = [
  'winRatio',
  'lossRatio',
];

export const RANKS_TO_SHOW = [
  'hub',
  'state',
  'country',
];

export const LOL_QUEUE_TYPES = {
  top: 'top',
  mid: 'mid',
  jungle: 'jungle',
  adc: 'bot',
  support: 'support',
};

export const LOL_STATS_TYPES = {
  winRatio: 'percent',
  lossRatio: 'percent',
  goldShare: 'percent',
  damageShare: 'percent',
};

export const LOL_STATS_INVERTED = {
};

export const lolRankScaleFactor = {
  'IRON_IV': 0,
  'IRON_III': 100,
  'IRON_II': 200,
  'IRON_I': 300,
  'BRONZE_IV': 400,
  'BRONZE_III': 500,
  'BRONZE_II': 600,
  'BRONZE_I': 700,
  'SILVER_IV': 800,
  'SILVER_III': 900,
  'SILVER_II': 1000,
  'SILVER_I': 1100,
  'GOLD_IV': 1200,
  'GOLD_III': 1300,
  'GOLD_II': 1400,
  'GOLD_I': 1500,
  'PLATINUM_IV': 1600,
  'PLATINUM_III': 1700,
  'PLATINUM_II': 1800,
  'PLATINUM_I': 1900,
  'DIAMOND_IV': 2000,
  'DIAMOND_III': 2100,
  'DIAMOND_II': 2200,
  'DIAMOND_I': 2300,
  'MASTER_I': 2400,
  'GRANDMASTER_I': 2500,
  'CHALLENGER_I': 2600,
};

export function getFeaturedStatisticsData(gameStats, profileGeneralDatal) {
  return {
    avatar: profileGeneralDatal.avatar,
    rankAvatar: getLolRankAvatar(gameStats.general.tier, gameStats.general.rank), // Using LoL rank
    name: gameStats.general.playerName || 'unknown',
    rank: {
      name: getRankName(gameStats.general.tier, gameStats.general.rank), // ex: Platinum IV or UNRANKED
      info: `LP ${gameStats.general.lp}`,
    },
  };
}

export function getQueuesStats(queuesMap) {
  const queues = [];

  Object.keys(LOL_QUEUE_TYPES).forEach(queueName => {
    const queueStats = queuesMap[queueName] || {};
    const queueDisabled = !Object.keys(queueStats).length;
    const stats = [];

    LOL_QUEUES_STATS_ORDER.forEach(statName => {
      stats.push({
        name: LOL_STATS_NAME[statName],
        nameAbbr: LOL_STATS_NAME_ABBR[statName] || LOL_STATS_NAME[statName],
        value: queueStats[statName] || 0,
        inverted: LOL_STATS_INVERTED[statName],
        symbol: ProfileHelpers.parseStatSymbol(statName, LOL_STATS_TYPES),
      });
    });

    const championsList = [];
    if (!queueDisabled) {
      queueStats.champions.forEach(champion => {
        const featuredStats = [];
        LOL_QUEUES_STATS_ORDER.forEach(statName => {
          featuredStats.push({
            name: LOL_STATS_NAME[statName],
            value: champion[statName],
            icon: statName,
            symbol: ProfileHelpers.parseStatSymbol(statName, LOL_STATS_TYPES),
          });
        });

        const collapsedStats = [];
        ['winRatio', 'kda'].forEach(statName => {
          collapsedStats.push({
            name: LOL_STATS_NAME[statName],
            value: champion[statName],
            symbol: ProfileHelpers.parseStatSymbol(statName, LOL_STATS_TYPES),
          });
        });

        const expandedStats = [];
        ['mastery'].forEach(statName => {
          expandedStats.push({
            name: LOL_STATS_NAME[statName],
            icon: `mastery-${champion.mastery}`,
            value: champion.mastery,
            symbol: ProfileHelpers.parseStatSymbol(statName, LOL_STATS_TYPES),
          });
        });

        if (championsMap[champion.championId]) {
          const obj = {
            avatar: championsMap[champion.championId].nameId, // Example: Velkoz
            name: championsMap[champion.championId].name, // Example: Vel'koz
            mainStat: `${champion.matches} games`,
            matches: champion.matches,
            featuredStats,
            expandedStats,
            collapsedStats,
          };

          championsList.push(obj);
        }
      });
    }

    championsList.sort((a, b) => b.matches - a.matches); // Sort champions from most matches to less.

    const detailData = {
      title: `FAVORITE ${LOL_QUEUE_TYPES[queueName]} CHAMPIONS`,
      list: championsList,
    };

    queues.push({
      ...queueStats,
      icon: LOL_QUEUE_TYPES[queueName],
      type: LOL_QUEUE_TYPES[queueName],
      stats,
      detailData,
      disableDiff: true,
      disabled: queueDisabled,
    });
  });

  return queues;
}

export function getOverviewStats(gameData) {
  return { wins: gameData.general.wins, winRatio: gameData.general.winRatio };
}

export function getCoreStats(gameData) {
  const stats = [];
  let maxGraphValue = 0;

  LOL_CORE_STATS_ORDER.forEach(statName => {
    stats.push({
      name: LOL_STATS_NAME[statName],
      playerValue: gameData.general[statName],
      othersValue: gameData.general.others[statName],
      symbol: ProfileHelpers.parseStatSymbol(statName, LOL_STATS_TYPES),
    });

    maxGraphValue = gameData.general[statName] > maxGraphValue ? gameData.general[statName] : maxGraphValue;
    maxGraphValue = gameData.general.others[statName] > maxGraphValue ? gameData.general.others[statName] : maxGraphValue;
  });

  return { maxGraphValue, stats };
}
export function getCoreStatsDiff(current, opponent) {
  const currentKDA = current.stats.find(stat => stat.name === LOL_STATS_NAME.kda).playerValue;
  const opponentKDA = opponent.stats.find(stat => stat.name === LOL_STATS_NAME.kda).playerValue;
  // const currentCSD = current.stats.find(stat => stat.name === LOL_STATS_NAME.csd).playerValue;
  // const opponentCSD = opponent.stats.find(stat => stat.name === LOL_STATS_NAME.csd).playerValue;
  const currentGoldShare = current.stats.find(stat => stat.name === LOL_STATS_NAME.goldShare).playerValue;
  const opponentGoldShare = opponent.stats.find(stat => stat.name === LOL_STATS_NAME.goldShare).playerValue;
  const currentDamageShare = current.stats.find(stat => stat.name === LOL_STATS_NAME.damageShare).playerValue;
  const opponentDamageShare = opponent.stats.find(stat => stat.name === LOL_STATS_NAME.damageShare).playerValue;

  const diffKDA = ProfileHelpers.calculateDiffPercent(currentKDA, opponentKDA);
  // const diffCSD = ProfileHelpers.calculateDiffPercent(currentCSD, opponentCSD);
  const diffGoldShare = ProfileHelpers.calculateDiffPercent(currentGoldShare, opponentGoldShare);
  const diffDamageShare = ProfileHelpers.calculateDiffPercent(currentDamageShare, opponentDamageShare);

  // Christian Equation (rounded to 2 decimal places)
  const total = ((diffKDA + diffGoldShare + diffDamageShare) / 4);
  return total.toFixed();
}

export function getRankProgression(gameData) {
  const points = [];
  const times = [];
  const tooltips = [];
  const gridLabelImagesMap = generateRankImagesMap();
  const minMaxLP = getMinMaxLP(gameData.rankProgression);
  gameData.rankProgression.forEach(rankData => {
    const hasRank = rankData.tier.toUpperCase() !== 'UNRANKED';
    const rankNameWithoutDivision = hasRank ? `${rankData.tier} ${rankData.rank}` : 'Unranked';
    tooltips.push(rankNameWithoutDivision);
    points.push(calculateOwnScore(rankData.score, rankData.tier, rankData.rank, minMaxLP));
    // NOTE with .utc() it will take +1 day in negative Z zones (like argentina -3)
    times.push(dayjs.unix(rankData.date).toISOString());
  });

  return { points, times, gridLabelImagesMap, tooltips };
}

export function getMonthlyStats(gameData) {
  let monthAvgPerformance = 0;
  let overallPerformance = gameData.general.avgPerformance;
  overallPerformance = overallPerformance > 0 ? overallPerformance : 1;

  // We add the variable lossRatio to each day.
  const dailyActivity = gameData.activity.dailyActivity.concat().map(day => ({
    ...day,
    lossRatio: day.matches ? 100 - day.winRatio : 0,
  }));

  dailyActivity.forEach((day, i) => {
    monthAvgPerformance += day.avgPerformance;

    const stats = LOL_MONTHLY_STATS_TOOLTIP_ORDER.map(statName => {
      return {
        name: LOL_STATS_NAME[statName],
        value: day[statName],
        symbol: ProfileHelpers.parseStatSymbol(statName, LOL_STATS_TYPES),
      };
    });

    // Note: We add performancePercentage to day data.
    dailyActivity[i] = {
      ...day,
      stats,
      performancePercentage: day.matches ? ((day.avgPerformance * 100) / overallPerformance).toFixed() : 0,
    };
  });

  // Note: We add the performancePercentage to month data.
  monthAvgPerformance = monthAvgPerformance / gameData.activity.dailyActivity.length;
  monthAvgPerformance = ((monthAvgPerformance * 100) / overallPerformance).toFixed();

  return {
    days: dailyActivity,
    week: gameData.activity.weekActivity,
    month: {
      ...gameData.activity.monthActivity,
      performancePercentage: monthAvgPerformance,
    },
  };
}

export function getRankStats(gameData, generalData) {
  const stats = [];
  RANKS_TO_SHOW.forEach(rankName => {
    if (!gameData.rank[rankName]) {
      return;
    }

    if (rankName === 'hub') {
      if (!generalData.hubMembership[0]) {
        return;
      }
      const selectedHubData = generalData.hubMembership[0];
      // When select hub feature done in backend.
      // const selectedHubData = generalData.hubMembership.find(hub => hub.id === gameData.rank.selectedHub);
      if (!selectedHubData) {
        return;
      }

      stats.push({ rank: `#${gameData.rank[rankName]} rank`, location: selectedHubData.name, icon: rankName });
      return;
    }

    stats.push({ rank: `#${gameData.rank[rankName]} rank`, location: generalData[rankName], icon: rankName });
  });

  return {
    playerName: gameData.general.playerName,
    rankAvatar: getLolRankAvatar(gameData.general.tier, gameData.general.rank), // Using LoL rank
    rank: {
      name: getRankName(gameData.general.tier, gameData.general.rank), // ex: Platinum IV or UNRANKED
      info: `LP ${gameData.general.lp}`,
    },
    stats,
  };
}

export function getleaderboardListWithStatName(leaderboardList) {
  return leaderboardList.map(player => {
    return { ...player, statName: LOL_STATS_NAME[player.leaderboardType] };
  });
}

function getLolRankAvatar(tier, rank) {
  let rankAvatar = 'lol/';
  const rankNumber = parseLolRomanRanks(rank);

  // NOTE If we have rank we display an rankAvatar like `platinum_4.png`
  // if we DON'T have rank, we just display `platinum_${rank}.png` or `unranked.png`
  if (tier && tier.toUpperCase() !== 'UNRANKED') {
    rankAvatar = rankAvatar + tier.toLowerCase();
    if (rankNumber) {
      rankAvatar = rankAvatar + '_' + rankNumber;
    }
  } else {
    rankAvatar = rankAvatar + 'provisional';
  }

  return rankAvatar;
}

function parseLolRomanRanks(romanLolRank) {
  if (!romanLolRank) {
    return NaN;
  }
  switch (romanLolRank.toUpperCase()) {
    case 'I':
      return '1';
    case 'II':
      return '2';
    case 'III':
      return '3';
    case 'IV':
      return '4';

    default:
      return romanLolRank;
  }
}

function getMinMaxLP(rankProgression) {
  var minRank = 1000;
  var maxRank = 0;
  rankProgression.forEach(rankData => {
    if (minRank > rankData.score && minRank > 0) {
      minRank = rankData.score;
    }
    if (maxRank < rankData.score) {
      maxRank = rankData.score;
      if (maxRank > 999) {
        maxRank = 999;
      }
    }
  });
  return { minRank, maxRank };
}

function calculateOwnScore(score, tier, rank, minMaxLP) {
  if (!tier || (tier && tier.toUpperCase() === 'UNRANKED')) {
    return 0;
  }
  const key = `${tier}_${rank}`;
  if (lolRankScaleFactor[key] >= lolRankScaleFactor['GRANDMASTER_I']) {
    if (minMaxLP['maxRank'] > 100) {
      if (score >= minMaxLP['minRank'] && score <= minMaxLP['maxRank']) {
        score = (score - minMaxLP['minRank']) / 10;
      } else if (score < minMaxLP['minRank']) {
        score = 0;
      } else if (score > minMaxLP['maxRank']) {
        score = 999 / 10;
      }
    }
  }
  return lolRankScaleFactor[key] + parseFloat(score.toPrecision(1));
}

function getRankName(tier, rank) {
  const rankName = (tier && tier.toUpperCase() !== 'UNRANKED') ? `${tier} ${rank}` : tier; // ex: Platinum IV or UNRANKED
  return rankName;
}

function generateRankImagesMap() {
  const rankMap = {};
  Object.keys(lolRankScaleFactor).forEach(rankName => {
    const score = lolRankScaleFactor[rankName];
    rankMap[score] = '/assets/ranks/lol/' + humanizeRank(rankName);
  });

  return rankMap;
}

function humanizeRank(rankString) {
  const rankArray = rankString.split('_');
  const rank = rankArray[0].toLowerCase();
  const tier = parseLolRomanRanks(rankArray[1]);

  return `${rank}_${tier}`;
}
